import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CustomTextField from "../components/CustomTextField";
import CustomDropdown from "../components/CustomDropdown";
import CustomButton from "../components/CustomButton";
import ReusableHeader from "./ReusableHeader";
import "../styles/layout.css";
import { toast } from "react-toastify";
import { useUsersApi } from "../api/users";
import { useOrganizationApi } from "../api/organization";
import { decodeJwt } from "../util/decodeJwt";
import OrganizationCreateDialog from "./OrganizationCreateDialog";
import { validatePassword, validatePhoneNumber } from "../util/validation";

export default function ProfileUpdateWidget({ onSubmit, onCancel }) {
  const { getVendorUserById, updateVendorUser, registerVendorUser } = useUsersApi();
  const { getVendorOrgById } = useOrganizationApi();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    password: "",
    role: "",
    status: "",
    address: "",
    organizationId: "",
  });

  const [loading, setLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [userId, setUserId] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [orgId, setOrgId] = useState("");

  // Organization dialog state
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const handleOrgDialogOpen = () => setIsOrgDialogOpen(true);
  const handleOrgDialogClose = () => setIsOrgDialogOpen(false);

  const handleOrgCreated = (newOrg) => {
    setOrgOptions((prev) => [...prev, { value: newOrg._id, label: newOrg.name }]);
    setFormData((prev) => ({ ...prev, organizationId: newOrg._id }));
    setIsOrgDialogOpen(false);
  };

  // Decode JWT to get current user id + role
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = decodeJwt(token);
        if (decoded?._id) setUserId(decoded._id);
        if (decoded?.role) setCurrentUserRole(decoded.role.toLowerCase());
        setOrgId(decoded.organizationId || "");
      } catch (err) {
        console.error("JWT decode failed", err);
      }
    }
  }, []);

  // Role dropdown options based on logged-in user role
  const getRoleOptions = async () => {
    let targetUserRole = null;
    if (isEditMode) {
      const user = await getVendorUserById(id);
      targetUserRole = user?.role?.toLowerCase();
    }

    const loggedInRole = currentUserRole?.toLowerCase();

    switch (loggedInRole) {
      case "admin":
        return [
          { value: "admin", label: "Admin" },
        ];

      case "manufacturer":
        if (targetUserRole === "manufacturer") {
          return [{ value: "manufacturer", label: "Manufacturer" }];
        }
      case "distributor":
        if (targetUserRole === "distributor") {
          return [{ value: "distributor", label: "Distributor" }];
        }
      case "dealer":
        if (targetUserRole === "dealer") {
          return [{ value: "dealer", label: "Dealer" }];
        }
      default:
        return [];
    }
  };

  // Load role options
  useEffect(() => {
    (async () => {
      if (currentUserRole) {
        const options = await getRoleOptions();
        setRoleOptions(options);

        if (options.length === 1 && !formData.role) {
          setFormData((prev) => ({ ...prev, role: options[0].value }));
        }
      }
    })();
  }, [currentUserRole, id, isEditMode]);

// Fetch organizations
useEffect(() => {
  async function loadOrganizations() {
    if (!userId || !currentUserRole) return;

    // Skip fetching organization if current user is admin
    if (currentUserRole.toLowerCase() === "admin") return;

    try {
      const result = await getVendorOrgById(orgId);

      const options = [
        {
          value: result._id,
          label: result.name,
        },
      ];

      setOrgOptions(options);

      if (options.length === 1) {
        setFormData((prev) => ({ ...prev, organizationId: options[0].value }));
      }
    } catch (err) {
      console.error("Failed to load organizations", err);
      toast.error("Failed to load organizations");
    }
  }
  loadOrganizations();
}, [userId, currentUserRole]);


  // Fetch user data if editing
  useEffect(() => {
    async function loadUser() {
      if (!isEditMode) return;
      try {
        setLoading(true);
        const user = await getVendorUserById(id);
        const isAdminUser = user.role?.toLowerCase() === "admin";

        setFormData({
          name: user.name || "",
          phoneNumber: user.phoneNumber || "",
          password: "",
          role: user.role || "",
          status: user.status || "active",

          // ✅ FIXED
          address: user.organization?.address || "",


          // safer organizationId
          organizationId: user.organization?._id || user.organizationId || "",
        });

        if (isAdminUser) {
          setCurrentUserRole("admin");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id, isEditMode]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
  const submitData = { ...formData };
  delete submitData.role;

  /* ===============================
     NAME VALIDATION
  =============================== */
  if (!formData.name?.trim()) {
    toast.error("Name is required");
    return;
  }

  /* ===============================
     PHONE VALIDATION
  =============================== */
  if (!formData.phoneNumber?.trim()) {
    toast.error("Phone number is required");
    return;
  }

  if (!validatePhoneNumber(formData.phoneNumber)) {
    toast.error("Phone number must be 10 digits");
    return;
  }

  /* ===============================
     PASSWORD VALIDATION
     Create → required
     Edit → optional
  =============================== */

  if (!isEditMode) {
    if (!formData.password?.trim()) {
      toast.error("Password is required");
      return;
    }

    if (!validatePassword(formData.password)) {
      toast.error(
        "Password must be 8+ characters with at least 1 letter and 1 number"
      );
      return;
    }
  }

  // Edit mode → validate only if entered
  if (isEditMode && formData.password?.trim()) {
    if (!validatePassword(formData.password)) {
      toast.error(
        "Password must be 8+ characters with at least 1 letter and 1 number"
      );
      return;
    }
  }

  /* ===============================
   ADDRESS VALIDATION
  =============================== */

  if (!formData.address?.trim()) {
    toast.error("Address is required");
    return;
  }

  /* ===============================
     ORGANIZATION VALIDATION
  =============================== */

  if (
    currentUserRole?.toLowerCase() !== "admin" &&
    !formData.organizationId
  ) {
    toast.error("Please select organization");
    return;
  }

  /* ===============================
     API CALL
  =============================== */

  try {
    setLoading(true);

    let result;

    if (isEditMode) {
      result = await updateVendorUser(id, submitData);
      toast.success("User updated successfully!");
    } else {
      result = await registerVendorUser(submitData);
      toast.success("User registered successfully!");
    }

    if (onSubmit) onSubmit(result.data || submitData);
    navigate("/");

  } catch (err) {
    console.error(err);
    toast.error(err.message || "Operation failed");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="user-container">
      <ReusableHeader title={isEditMode ? "Edit User" : "Create User"} />

      <div className="user-form-card">
        <div className="user-columns">
          <div className="user-column-left">
            <CustomTextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
            <CustomTextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
            />
            <CustomTextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
          </div>

          <div className="user-column-right">

            <CustomDropdown
              label="Role"
              options={roleOptions}
              value={formData.role}
              onChange={(value) => handleChange("role", value)}
              isMultiSelect={false}
              disabled={false}
            />

            <CustomTextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </div>
        </div>

        <div className="user-buttons">
          <CustomButton
            label="Cancel"
            className="user-cancel"
            onClick={() => navigate(-1)}
          />
          <CustomButton
            label={
              loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update"
                : "Create"
            }
            className="user-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
